import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { apiJson } from "../utils/api";
import "./WhatsAppInbox.css";

const formatTime = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDateTime = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const branchNameById = branches => {
  const map = new Map();
  (Array.isArray(branches) ? branches : []).forEach(branch => {
    const id = String(branch.publicId || "").trim();
    if (!id) return;
    map.set(id, branch.branchName || branch.branchCode || id);
  });
  return map;
};

const WhatsAppInbox = ({ currentUser }) => {
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [search, setSearch] = useState("");
  const [conversations, setConversations] = useState([]);
  const [selectedConversationRef, setSelectedConversationRef] = useState("");
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState("");
  const [attachmentType, setAttachmentType] = useState("");
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const branchMap = useMemo(() => branchNameById(branches), [branches]);
  const defaultAllowedBranchPublicId = currentUser?.allowedBranchPublicIds?.length === 1
    ? String(currentUser.allowedBranchPublicIds[0] || "")
    : "";

  // The selected conversation is derived from the live conversations list rather than
  // stored as its own object, so periodic background refreshes never force a new
  // object reference (which previously re-triggered message fetches and reset scroll).
  const selectedConversation = useMemo(
    () => conversations.find(item => item.conversationRef === selectedConversationRef) || null,
    [conversations, selectedConversationRef]
  );
  const selectedClientPublicId = selectedConversation?.clientPublicId || "";
  const selectedMobile = selectedConversation?.mobile || "";

  // Some webhook status updates (sent/delivered/read) can't be matched back to their
  // original outbound row (e.g. a re-delivered or out-of-order webhook) and land in the
  // DB as their own contentless "status echo" row. Rather than rendering those as bogus
  // extra bubbles ("Status: delivered"), fold each one into the most recent real outbound
  // message instead, so the thread only ever shows the latest status on the message itself.
  const displayMessages = useMemo(() => {
    const merged = [];
    let lastOutboundIndex = -1;
    messages.forEach(item => {
      const outbound = String(item.direction || "").toLowerCase() === "outbound";
      const hasContent = !!(String(item.messageText || "").trim() || item.attachmentUrl);
      if (outbound && !hasContent && item.status) {
        if (lastOutboundIndex >= 0) {
          const target = merged[lastOutboundIndex];
          merged[lastOutboundIndex] = {
            ...target,
            status: item.status,
            statusDetail: item.statusDetail || target.statusDetail,
            eventTimestamp: item.eventTimestamp || target.eventTimestamp
          };
        }
        return;
      }
      merged.push(item);
      if (outbound) lastOutboundIndex = merged.length - 1;
    });
    return merged;
  }, [messages]);

  const threadBodyRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const lastScrollConversationRefRef = useRef("");
  const messagesSignatureRef = useRef("");
  const sendInFlightRef = useRef(false);

  useEffect(() => {
    apiJson("/api/branches")
      .then(data => setBranches(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error("Failed to load branches", err);
      });
  }, []);

  useEffect(() => {
    if (branchFilter) return;
    if (defaultAllowedBranchPublicId) {
      setBranchFilter(defaultAllowedBranchPublicId);
    }
  }, [branchFilter, defaultAllowedBranchPublicId]);

  const loadConversations = async ({ silent } = {}) => {
    try {
      if (!silent) {
        setLoadingConversations(true);
        setError("");
      }
      const params = new URLSearchParams();
      if (branchFilter) params.set("branchPublicId", branchFilter);
      if (search.trim()) params.set("q", search.trim());
      const list = await apiJson(`/api/whatsapp/conversations?${params.toString()}`);
      const normalized = Array.isArray(list) ? list : [];
      setConversations(normalized);

      // Only auto-select the first conversation when nothing is selected yet.
      // Never overwrite an existing selection here - the selected conversation
      // object is derived from this same list, so it updates automatically.
      setSelectedConversationRef(prev => (prev ? prev : (normalized[0]?.conversationRef || "")));
    } catch (err) {
      console.error("Failed to load WhatsApp conversations", err);
      if (!silent) {
        setError(err.message || "Could not load conversations");
      }
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  };

  useEffect(() => {
    loadConversations();
    const timer = setInterval(() => loadConversations({ silent: true }), 25000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter]);

  useEffect(() => {
    return () => {
      if (attachmentPreview) {
        URL.revokeObjectURL(attachmentPreview);
      }
    };
  }, [attachmentPreview]);

  // Reset the thread whenever the selected conversation identity changes, so the
  // previous conversation's messages never briefly flash inside the new thread.
  useEffect(() => {
    messagesSignatureRef.current = "";
    setMessages([]);
  }, [selectedConversationRef]);

  const fetchMessages = useCallback(async ({ silent } = {}) => {
    if (!selectedConversationRef) {
      setMessages([]);
      messagesSignatureRef.current = "";
      return;
    }

    try {
      if (!silent) {
        setLoadingMessages(true);
        setError("");
      }
      const params = new URLSearchParams();
      if (selectedClientPublicId) {
        params.set("clientPublicId", selectedClientPublicId);
      } else if (selectedMobile) {
        params.set("mobile", selectedMobile);
      }
      if (branchFilter) {
        params.set("branchPublicId", branchFilter);
      }
      params.set("limit", "150");
      const rows = await apiJson(`/api/whatsapp/messages?${params.toString()}`);
      const list = Array.isArray(rows) ? rows : [];
      // Skip the state update entirely when nothing actually changed - this is what
      // stops the periodic poll from forcing an unnecessary re-render (and the
      // resulting scroll jump) when the conversation has no new activity.
      const signature = list.map(item => `${item.publicId || item.id}:${item.status || ""}`).join("|");
      if (signature !== messagesSignatureRef.current) {
        messagesSignatureRef.current = signature;
        setMessages(list);
      }
    } catch (err) {
      console.error("Failed to load WhatsApp messages", err);
      if (!silent) {
        setError(err.message || "Could not load messages");
      }
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, [selectedConversationRef, selectedClientPublicId, selectedMobile, branchFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Poll only the active conversation's messages frequently so incoming replies
  // show up without noticeable delay, independent of the slower conversation list poll.
  useEffect(() => {
    if (!selectedConversationRef) return undefined;
    const timer = setInterval(() => {
      fetchMessages({ silent: true });
    }, 6000);
    return () => clearInterval(timer);
  }, [selectedConversationRef, fetchMessages]);

  const handleSelectConversation = conversation => {
    const nextRef = conversation?.conversationRef || "";
    if (nextRef === selectedConversationRef) return;
    setSelectedConversationRef(nextRef);
  };

  const handleThreadScroll = () => {
    const el = threadBodyRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = distanceFromBottom < 120;
  };

  // Keeps the scroll position stable: jumps to the bottom only when switching
  // conversations or when the agent was already at the bottom, and otherwise
  // leaves the reading position untouched (e.g. while reviewing older messages).
  useLayoutEffect(() => {
    const el = threadBodyRef.current;
    if (!el) return;

    const conversationChanged = lastScrollConversationRefRef.current !== selectedConversationRef;
    lastScrollConversationRefRef.current = selectedConversationRef;

    if (conversationChanged) {
      isAtBottomRef.current = true;
      el.scrollTop = el.scrollHeight;
      return;
    }

    if (isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, selectedConversationRef]);

  const uploadAttachment = async () => {
    if (!attachmentFile) return null;
    const formData = new FormData();
    formData.append("file", attachmentFile);
    setUploadingAttachment(true);
    try {
      const result = await apiJson("/api/whatsapp/attachments", {
        method: "POST",
        body: formData
      });
      return {
        publicUrl: result?.publicUrl || "",
        attachmentType: String(result?.attachmentType || "").trim().toLowerCase(),
        fileName: String(result?.originalName || attachmentFile.name || "").trim()
      };
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleSend = async () => {
    // Guard with a ref (not just state) so a rapid double-click can't slip through
    // before the `sending` state update has actually re-rendered the button.
    if (sendInFlightRef.current) return;
    if (!selectedConversation || uploadingAttachment) return;
    const text = String(messageText || "").trim();
    if (!text && !attachmentFile) return;

    sendInFlightRef.current = true;
    setSending(true);
    setError("");

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimisticMessage = {
      publicId: tempId,
      direction: "outbound",
      messageType: attachmentFile ? (attachmentType === "document" ? "document" : "image") : "text",
      messageText: text,
      attachmentUrl: attachmentPreview || "",
      status: "sending",
      eventTimestamp: new Date().toISOString(),
      createdAt: new Date().toISOString()
    };
    // Show the message immediately with a "sending" status, and stick to the bottom
    // since the agent just triggered this send.
    isAtBottomRef.current = true;
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const uploadedAttachment = attachmentFile ? await uploadAttachment() : null;
      if (attachmentFile && !uploadedAttachment?.publicUrl) {
        throw new Error("The attachment could not be uploaded. Please try again.");
      }
      await apiJson("/api/whatsapp/messages", {
        method: "POST",
        body: JSON.stringify({
          clientPublicId: selectedConversation.clientPublicId || "",
          mobile: selectedConversation.mobile || "",
          message: text,
          attachmentUrl: uploadedAttachment?.publicUrl || "",
          attachmentType: uploadedAttachment?.attachmentType || attachmentType || "",
          attachmentName: uploadedAttachment?.fileName || attachmentFile?.name || "",
          caption: text,
          branchPublicId: selectedConversation.branchPublicId || branchFilter || ""
        })
      });

      setMessageText("");
      setAttachmentFile(null);
      setAttachmentPreview("");
      setAttachmentType("");
      if (fileInputRef.current) fileInputRef.current.value = "";

      // Force the next fetch to replace the optimistic bubble with the persisted message.
      messagesSignatureRef.current = "";
      await Promise.all([loadConversations({ silent: true }), fetchMessages()]);
    } catch (err) {
      console.error("Failed to send WhatsApp message", err);
      const reason = err.message || "Could not send message";
      setError(reason);
      // Preserve the typed content (messageText/attachment are left untouched) so the
      // agent can retry, and mark the optimistic bubble as failed instead of "sent".
      setMessages(prev => prev.map(item => (
        item.publicId === tempId
          ? { ...item, status: "failed", statusDetail: reason }
          : item
      )));
    } finally {
      setSending(false);
      sendInFlightRef.current = false;
    }
  };

  const handleAttachmentChange = e => {
    const file = e.target.files?.[0] || null;
    if (attachmentPreview) {
      URL.revokeObjectURL(attachmentPreview);
    }
    setAttachmentFile(file);
    setAttachmentType(file ? (String(file.type || "").startsWith("image/") ? "image" : "document") : "");
    setAttachmentPreview(file && String(file.type || "").startsWith("image/") ? URL.createObjectURL(file) : "");
  };

  const clearConversation = async conversation => {
    if (!conversation) return;
    const name = conversation.clientName || conversation.mobile || "this contact";
    if (!window.confirm(`Clear chat for ${name}? This removes its WhatsApp message history.`)) return;

    try {
      setError("");
      await apiJson("/api/whatsapp/messages", {
        method: "DELETE",
        body: JSON.stringify({
          clientPublicId: conversation.clientPublicId || "",
          mobile: conversation.mobile || "",
          branchPublicId: conversation.branchPublicId || branchFilter || ""
        })
      });

      if (selectedConversationRef === conversation.conversationRef) {
        setSelectedConversationRef("");
        setMessages([]);
      }
      await loadConversations();
    } catch (err) {
      setError(err.message || "Could not clear chat");
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    await clearConversation(selectedConversation);
  };

  const handleClearChats = async () => {
    const clearBranchPublicId = branchFilter || selectedConversation?.branchPublicId || "";
    const confirmText = clearBranchPublicId
      ? "Clear all chats for the current branch filter?"
      : "Clear all accessible WhatsApp chats?";
    if (!window.confirm(confirmText)) return;

    try {
      setError("");
      await apiJson("/api/whatsapp/messages", {
        method: "DELETE",
        body: JSON.stringify({
          clearAll: true,
          branchPublicId: clearBranchPublicId
        })
      });
      setSelectedConversationRef("");
      setMessages([]);
      await loadConversations();
    } catch (err) {
      setError(err.message || "Could not clear chats");
    }
  };

  return (
    <div className="whatsapp-page page main-content">
      <div className="whatsapp-header">
        <div>
          <h1>WhatsApp Inbox</h1>
          <p>Branch-aware customer messaging with a live chat-style timeline.</p>
        </div>
        <div className="whatsapp-header-actions">
          <button type="button" className="refresh-btn" onClick={loadConversations}>Refresh</button>
          <button type="button" className="refresh-btn danger" onClick={handleClearChats}>Clear Chats</button>
        </div>
      </div>

      <div className="whatsapp-toolbar">
        <input
          type="text"
          className="whatsapp-search"
          placeholder="Search by client, mobile, or last message"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") loadConversations();
          }}
        />
        <select className="whatsapp-branch-filter" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
          <option value="">All branches</option>
          {branches.map(branch => (
            <option key={branch.publicId || branch.id} value={branch.publicId}>{branch.branchName || branch.branchCode}</option>
          ))}
        </select>
      </div>

      {error ? <p className="whatsapp-error">{error}</p> : null}

      <div className="whatsapp-shell">
        <aside className="conversation-pane">
          {loadingConversations ? <p className="pane-hint">Loading conversations...</p> : null}
          {!loadingConversations && conversations.length === 0 ? <p className="pane-hint">No conversations found.</p> : null}
          <div className="conversation-list">
            {conversations.map(conversation => {
              const isActive = selectedConversation?.conversationRef === conversation.conversationRef;
              const branchLabel = branchMap.get(String(conversation.branchPublicId || "")) || "Unmapped branch";
              return (
                <button
                  type="button"
                  key={conversation.conversationRef}
                  className={`conversation-card ${isActive ? "active" : ""}`.trim()}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  <div className="conversation-avatar">{(conversation.clientName || "?").slice(0, 1).toUpperCase()}</div>
                  <div className="conversation-meta">
                    <div className="conversation-top-row">
                      <strong>{conversation.clientName || "Unknown client"}</strong>
                      <span>{formatTime(conversation.lastTimestamp)}</span>
                    </div>
                    <div className="conversation-middle-row">
                      <span>{conversation.mobile || "No number"}</span>
                      {conversation.unreadCount > 0 ? <em>{conversation.unreadCount}</em> : null}
                    </div>
                    <p>{conversation.lastMessageText || "No message text"}</p>
                    <small>{branchLabel}</small>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="chat-pane">
          {!selectedConversation ? (
            <div className="empty-thread">Select a conversation to view messages.</div>
          ) : (
            <>
              <header className="thread-header">
                <div>
                  <h2>{selectedConversation.clientName || "Unknown client"}</h2>
                  <p>{selectedConversation.mobile || "No number"}</p>
                </div>
                <div className="thread-header-actions">
                  <span className="thread-branch-badge">
                    {branchMap.get(String(selectedConversation.branchPublicId || "")) || "Unmapped branch"}
                  </span>
                  <button type="button" className="thread-action-btn" onClick={handleDeleteConversation}>
                    Clear Chat
                  </button>
                </div>
              </header>

              <div className="thread-body" ref={threadBodyRef} onScroll={handleThreadScroll}>
                {loadingMessages ? <p className="pane-hint">Loading messages...</p> : null}
                {!loadingMessages && displayMessages.length === 0 ? <p className="pane-hint">No messages for this conversation.</p> : null}
                {displayMessages.map(item => {
                  const outbound = String(item.direction || "").toLowerCase() === "outbound";
                  const type = String(item.messageType || "").toLowerCase();
                  const isImage = !!item.attachmentUrl && type === "image";
                  const isDocument = !!item.attachmentUrl && type === "document";
                  const isFailed = outbound && String(item.status || "").toLowerCase() === "failed";
                  return (
                    <div key={item.publicId || item.id} className={`message-row ${outbound ? "outbound" : "inbound"}`.trim()}>
                      <div className={`message-bubble ${isFailed ? "failed" : ""}`.trim()} title={formatDateTime(item.eventTimestamp || item.createdAt)}>
                        {isImage ? (
                          <a href={item.attachmentUrl} className="message-media" target="_blank" rel="noreferrer">
                            <img src={item.attachmentUrl} alt={item.messageText || "WhatsApp attachment"} />
                          </a>
                        ) : null}
                        {isDocument ? (
                          <a href={item.attachmentUrl} className="message-doc-link" target="_blank" rel="noreferrer">
                            {item.messageText || "Open document"}
                          </a>
                        ) : null}
                        <p>{item.messageText || "(no content)"}</p>
                        {isFailed && item.statusDetail ? <p className="message-error-detail">{item.statusDetail}</p> : null}
                        <div className="message-meta">
                          <span>{formatTime(item.eventTimestamp || item.createdAt)}</span>
                          {outbound ? <span>{item.status || "sent"}</span> : null}
                          {isFailed ? (
                            <button
                              type="button"
                              className="message-retry-btn"
                              onClick={() => setMessageText(item.messageText || "")}
                            >
                              Retry
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <footer className="thread-composer">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  className="thread-file-input"
                  onChange={handleAttachmentChange}
                />
                <button type="button" className="attach-btn" onClick={() => fileInputRef.current?.click()} disabled={sending || uploadingAttachment}>
                  {attachmentFile ? "Change File" : "Attach File"}
                </button>
                <textarea
                  value={messageText}
                  onChange={e => setMessageText(e.target.value)}
                  placeholder="Type a WhatsApp message"
                  rows={2}
                />
                {attachmentPreview ? (
                  <div className="attachment-preview">
                    <img src={attachmentPreview} alt="Attachment preview" />
                    <button
                      type="button"
                      onClick={() => {
                        setAttachmentFile(null);
                        setAttachmentPreview("");
                        setAttachmentType("");
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
                {!attachmentPreview && attachmentFile ? (
                  <div className="attachment-file-pill">
                    <span>{attachmentFile.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setAttachmentFile(null);
                        setAttachmentType("");
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
                <button type="button" onClick={handleSend} disabled={sending || uploadingAttachment || (!messageText.trim() && !attachmentFile)}>
                  {sending || uploadingAttachment ? "Sending..." : "Send"}
                </button>
              </footer>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default WhatsAppInbox;
